import { withAuthorPanelSwitch, withStandardBaseCssClass } from '@adobe/aem-core-components-react-spa'
import { ComponentMapping, Container } from '@adobe/aem-react-editable-components'
import { Constants } from '@adobe/aem-spa-page-model-manager'
import Carousel from 'react-multi-carousel'

import 'react-multi-carousel/lib/styles.css'
import ReactArticleCarousel from './ReactArticleCarousel'
import ReactImageCarousel from './ReactImageCarousel'
import ReactLookingForMoreCarousel from './ReactLookingForMoreCarousel'
import ReactMixAndMatchCarousel from './ReactMixAndMatchCarousel'
import ReactProductCarousel from './ReactProductCarousel'
import ReactPSUCarousel from './ReactPSUCarousel'
import './ReactCarousel.scss'
import {
  AnimatedCarouselState,
  CarouselTypes,
  IProductContentFragment,
  IReactCarouselProperties,
  ReactCarouselProps
} from './types'
import { removeEmptyCF } from '../../utils/rule-builder-util'
import { getSpacing } from '../../utils/spacing-util'
import { ScreenSizes } from '../../utils/types'
import IsInEditorText from '../shared/IsInEditorText'

const formatFn = (value: string, args: string[]) => {
  let content = value
  for (let i = 0; i < args.length; i++) {
    const replacement = '{' + i + '}'
    content = content.replace(replacement, args[i])
  }
  return content
}

class ReactCarousel extends Container<ReactCarouselProps, AnimatedCarouselState> {
  interval = 0
  carouselItems: JSX.Element[]
  is2up: boolean
  carousel: Carousel | null
  static defaultProps = {
    _allowedComponentPlaceholderListEmptyLabel: 'CarouselV1',
    isInEditor: false,
    autoplay: false,
    cqPath: '',
    cqItems: {},
    cqItemsOrder: [],
    accessibilityLabel: 'Carousel',
    accessibility: {
      play: 'Play',
      pause: 'Pause',
      next: 'Next',
      previous: 'Previous',
      slide: 'Slide {0} of {1}',
      indicator: 'Slide %{0}',
      indicators: 'Choose a slide to display'
    }
  }

  constructor(props: ReactCarouselProps) {
    super(props)
    this.state = {
      activeIndex: 0,
      previousIndex: 0,
      isMouseEntered: false,
      additionalTransfrom: 0,
      width: 0,
      autoPlay: this.props.autoplay && !this.props.isInEditor,
      componentMapping: this.props.componentMapping ?? ComponentMapping
    }
    this.carouselItems = []
    this.is2up = false
    this.carousel = null
  }

  updateDimensions = () => {
    this.setState({ width: window.innerWidth })
  }

  componentDidUpdate(prevProps: Readonly<IReactCarouselProperties>): void {
    const { activeIndexFromAuthorPanel } = this.props
    if (activeIndexFromAuthorPanel !== undefined && prevProps.activeIndexFromAuthorPanel != activeIndexFromAuthorPanel) {
      this.setState({ activeIndex: activeIndexFromAuthorPanel })
    }
  }
  handleGoToSlide = (e: any) => {
    const slideId = e.detail.slideId // Remove the "#" character
    this.setState({ selectedSlideId: slideId })
  }
  resetSlideId = () => {
    this.setState({ selectedSlideId: '' })
  }
  componentDidMount() {
    window.addEventListener('resize', this.updateDimensions)
    window?.addEventListener('goToSlide', this.handleGoToSlide)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.updateDimensions)
    window?.removeEventListener('goToSlide', this.handleGoToSlide)
  }

  handleIndicatorClick(index: number) {
    this.setState({ previousIndex: this.state.activeIndex })
    if (this.state.activeIndex !== index) {
      this.setState({
        activeIndex: index
      })
    }
  }

  displayItem(item: JSX.Element, index: number) {
    const { isInEditor, baseCssClass, accessibility, cqItemsOrder } = this.props
    const isActive = index === this.state.activeIndex
    // we display the item if active is true, or if we are in the author mode.
    // we need to always display the item for the author mode to work properly.
    const display = !!(isActive || isInEditor)

    const cssClass = isActive ? `${baseCssClass}__item ${baseCssClass}__item--active` : `${baseCssClass}__item`
    const ariaLabel = formatFn(accessibility.slide, [(index + 1).toString(), cqItemsOrder.length.toString()])
    return (
      <div key={'item-' + index} className={cssClass} role='tabpanel' aria-label={ariaLabel} data-cmp-hook-carousel='item'>
        {display && item}
      </div>
    )
  }

  setMediaSize() {
    if (window.innerWidth > ScreenSizes.LG) {
      this.is2up = true
    } else {
      this.is2up = false
    }
  }

  getCarouselByType(activeAuthoredIndex: number) {
    const { cqItemsOrder, cqItems, carouselType } = this.props
    let wrapperClassByCarouselType = ''
    let editorText = ''
    let Component

    const itemsOrder = cqItemsOrder.length > 0 ? cqItemsOrder : this.props[Constants.ITEMS_ORDER_PROP]
    const items = Object.keys(cqItems).length > 0 ? cqItems : this.props[Constants.ITEMS_PROP]
    const contentFragments: IProductContentFragment[] = removeEmptyCF(itemsOrder.map((i: string) => items[i]))

    if (carouselType === CarouselTypes.ARTICLE) {
      wrapperClassByCarouselType = 'article'
      editorText = 'React Article Carousel'
      Component = (
        <ReactArticleCarousel
          {...this.props}
          activeAuthoredIndex={activeAuthoredIndex}
          contentFragments={contentFragments}
        />
      )
    } else if (carouselType === CarouselTypes.IMAGE) {
      wrapperClassByCarouselType = 'image'
      editorText = 'React Image Carousel'
      Component = (
        <ReactImageCarousel
          {...this.props}
          activeAuthoredIndex={activeAuthoredIndex}
          selectedSlideId={this.state.selectedSlideId}
          resetSlideId={this.resetSlideId}
        />
      )
    } else if (carouselType === CarouselTypes.PRODUCT) {
      wrapperClassByCarouselType = 'product'
      editorText = 'React Product Carousel'
      Component = (
        <ReactProductCarousel
          {...this.props}
          activeAuthoredIndex={activeAuthoredIndex}
          contentFragments={contentFragments}
          selectedSlideId={this.state.selectedSlideId}
          resetSlideId={this.resetSlideId}
        />
      )
    } else if (carouselType === CarouselTypes.MIX_AND_MATCH) {
      wrapperClassByCarouselType = 'mixandmatch'
      editorText = 'React Mix and Match Carousel'
      Component = <ReactMixAndMatchCarousel {...this.props} activeAuthoredIndex={activeAuthoredIndex} />
    } else if (carouselType === CarouselTypes.LOOKING_FOR_MORE) {
      wrapperClassByCarouselType = 'lookingformore'
      editorText = 'React Looking for More Carousel'
      Component = (
        <ReactLookingForMoreCarousel
          {...this.props}
          activeAuthoredIndex={activeAuthoredIndex}
          contentFragments={contentFragments}
          selectedSlideId={this.state.selectedSlideId}
          resetSlideId={this.resetSlideId}
        />
      )
    } else if (carouselType === CarouselTypes.PSU) {
      wrapperClassByCarouselType = 'psu'
      editorText = 'PSU Carousel'
      Component = (
        <ReactPSUCarousel
          {...this.props}
          activeAuthoredIndex={activeAuthoredIndex}
          contentFragments={contentFragments}
          selectedSlideId={this.state.selectedSlideId}
          resetSlideId={this.resetSlideId}
        />
      )
    } else {
      editorText = 'React Carousel'
    }
    const wrapperClass = `${wrapperClassByCarouselType}-carousel__content carousel-container`

    return { editorText, wrapperClass, Component }
  }

  renderCarousel() {
    const {
      cqItemsOrder = [],
      isInEditor = false,
      //carouselType = '',
      componentProperties,
      top = false,
      right = false,
      bottom = false,
      left = false,
      lob = ''
    } = this.props
    const carouselType = 'psu'
    const carouselTypes = [
      CarouselTypes.ARTICLE,
      CarouselTypes.PRODUCT,
      CarouselTypes.IMAGE,
      CarouselTypes.MIX_AND_MATCH,
      CarouselTypes.LOOKING_FOR_MORE,
      CarouselTypes.PSU
    ]
    const { activeIndexFromAuthorPanel } = componentProperties || {}
    const activeAuthoredIndex = activeIndexFromAuthorPanel
      ? cqItemsOrder.findIndex((item) => item === activeIndexFromAuthorPanel)
      : 0
    const { editorText, wrapperClass, Component } = this.getCarouselByType(activeAuthoredIndex)
    const hasCarouselType = carouselTypes.includes(carouselType as CarouselTypes)

    return (
      <>
        {IsInEditorText(isInEditor, editorText)}
        <div className={hasCarouselType ? `${wrapperClass}` : 'unselected'}>
          <div className={`carousel-grid ${getSpacing(top, right, bottom, left)} ${lob}`}>
            {this.childComponents.map((childComponent, index) => this.displayItem(childComponent, index))}
            {hasCarouselType ? Component : <>{isInEditor && <p className='editor-text'>Please select carousel type</p>}</>}
          </div>
        </div>
      </>
    )
  }

  render() {
    return (
      <div
        className={this.props.baseCssClass}
        role='group'
        data-panelcontainer='carousel'
        aria-label={this.props.accessibilityLabel}
        aria-roledescription='carousel'
        id={this.props.id}
      >
        {this.renderCarousel()}
      </div>
    )
  }
}

export default withStandardBaseCssClass(withAuthorPanelSwitch(ReactCarousel), 'cmp-carousel')
